import { useCallback, useEffect, useState } from 'react';

/**
 * Pinned feeds (#196 / control-panel spec §1, §2).
 *
 * An operator pins a camera (or, in future, a news feed) from the COP / detail
 * rail so it stays available across new selections. Pinned feeds live in the
 * operations panel (#197) media region — a separate same-origin window — so the
 * pin set has to be shared cross-window. We do that the same way the camera
 * pop-out registry does: a localStorage cache (the durable, first-paint source of
 * truth) plus a same-origin BroadcastChannel that pushes live updates to every
 * open window the instant a pin changes. A `storage` event is the belt-and-braces
 * fallback for any window that missed the broadcast.
 *
 * The pin *action* originates on the COP/rail; the pin *region* is the panel.
 *
 * News + drone feeds: the model carries a `kind` discriminator so each slots in
 * unchanged — `camera` (live cameras), `news` (#182 curated YouTube-live outlets),
 * and `drone` (#216 curated drone-feed stub) all ride the SAME pin store and
 * helpers; only the tile body that renders them differs (panel / wall).
 */

export const PINNED_FEEDS_KEY = 'tsr.pinnedFeeds';
export const PINNED_FEEDS_CHANNEL = 'tsr-pins';

export type PinnedFeedKind = 'camera' | 'news' | 'drone';

export interface PinnedFeed {
  kind: PinnedFeedKind;
  /** Stable id (camera uuid / news id) — the panel re-fetches the live feed by it. */
  id: string;
  /** Display label shown before/while the live feed resolves (e.g. camera name). */
  label: string;
}

interface PinsMessage {
  type: 'pins:update';
  pins: PinnedFeed[];
}

/** Stable key for a feed — two feeds match iff same kind AND id. */
export function feedKey(kind: PinnedFeedKind, id: string): string {
  return `${kind}:${id}`;
}

export function isPinned(pins: readonly PinnedFeed[], kind: PinnedFeedKind, id: string): boolean {
  return pins.some((p) => p.kind === kind && p.id === id);
}

/** Pure add — appends if not already present (most-recently-pinned last), so the
 *  visible-tile cap (§2) drops the *oldest* pins off-screen, not the newest. */
export function addPin(pins: readonly PinnedFeed[], feed: PinnedFeed): PinnedFeed[] {
  if (isPinned(pins, feed.kind, feed.id)) return pins.slice();
  return [...pins, feed];
}

export function removePin(
  pins: readonly PinnedFeed[],
  kind: PinnedFeedKind,
  id: string,
): PinnedFeed[] {
  return pins.filter((p) => !(p.kind === kind && p.id === id));
}

export function togglePin(pins: readonly PinnedFeed[], feed: PinnedFeed): PinnedFeed[] {
  return isPinned(pins, feed.kind, feed.id) ? removePin(pins, feed.kind, feed.id) : addPin(pins, feed);
}

function isPinnedFeed(v: unknown): v is PinnedFeed {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  return (
    (f.kind === 'camera' || f.kind === 'news' || f.kind === 'drone') &&
    typeof f.id === 'string' &&
    typeof f.label === 'string'
  );
}

/** Parse a stored/broadcast pin list defensively (drops any malformed entries). */
export function parsePins(raw: unknown): PinnedFeed[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isPinnedFeed).map((f) => ({ kind: f.kind, id: f.id, label: f.label }));
}

export function loadPins(): PinnedFeed[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    return parsePins(JSON.parse(localStorage.getItem(PINNED_FEEDS_KEY) ?? '[]'));
  } catch {
    return [];
  }
}

function savePins(pins: PinnedFeed[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PINNED_FEEDS_KEY, JSON.stringify(pins));
  } catch {
    /* quota / private mode — broadcast still syncs live windows this session. */
  }
}

/**
 * Replace the entire pin set in one beat (persist + broadcast) — the seat-profile
 * apply path (#187) restoring a profile's panel arrangement. Same write path as
 * the hook mutators (savePins + broadcast), so every open window (COP, /panel,
 * pop-outs) and every usePinnedFeeds instance converges on the new list; the
 * store's model and storage shape are unchanged.
 */
export function replaceAllPins(pins: PinnedFeed[]): void {
  const next = parsePins(pins);
  savePins(next);
  broadcast(next);
}

export interface PinnedFeedsApi {
  pins: PinnedFeed[];
  pin: (feed: PinnedFeed) => void;
  unpin: (kind: PinnedFeedKind, id: string) => void;
  toggle: (feed: PinnedFeed) => void;
  isPinned: (kind: PinnedFeedKind, id: string) => boolean;
}

/**
 * Cross-window pinned-feeds store. Every open window (COP, /panel, pop-out) gets
 * its own hook instance kept in lockstep: a local mutation writes localStorage and
 * broadcasts the new list; receivers replace their state (never re-broadcasting, so
 * there's no echo loop). Inert-but-safe where BroadcastChannel/localStorage are
 * unavailable (the list just stays window-local).
 */
export function usePinnedFeeds(): PinnedFeedsApi {
  const [pins, setPins] = useState<PinnedFeed[]>(() => loadPins());

  // Subscribe to live updates from sibling windows (channel) + the storage-event
  // fallback. Both just replace state with the incoming list (idempotent).
  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(PINNED_FEEDS_CHANNEL);
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        const data = e.data as PinsMessage | undefined;
        if (data?.type === 'pins:update') setPins(parsePins(data.pins));
      };
    }
    const onStorage = (e: StorageEvent): void => {
      if (e.key === PINNED_FEEDS_KEY) setPins(loadPins());
    };
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    return () => {
      channel?.close();
      if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Each mutator applies the change locally, persists to localStorage, and pushes
  // the new list to sibling windows in one beat (savePins + broadcast).
  const pin = useCallback((feed: PinnedFeed) => setPins((prev) => {
    const next = addPin(prev, feed);
    savePins(next);
    broadcast(next);
    return next;
  }), []);

  const unpin = useCallback((kind: PinnedFeedKind, id: string) => setPins((prev) => {
    const next = removePin(prev, kind, id);
    savePins(next);
    broadcast(next);
    return next;
  }), []);

  const toggle = useCallback((feed: PinnedFeed) => setPins((prev) => {
    const next = togglePin(prev, feed);
    savePins(next);
    broadcast(next);
    return next;
  }), []);

  return {
    pins,
    pin,
    unpin,
    toggle,
    isPinned: useCallback((kind, id) => isPinned(pins, kind, id), [pins]),
  };
}

function broadcast(pins: PinnedFeed[]): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(PINNED_FEEDS_CHANNEL);
  channel.postMessage({ type: 'pins:update', pins } satisfies PinsMessage);
  channel.close();
}
